<template>
  <div class="page_container pa-5">
    <div v-if="loading && !roundNotFound" class="loading">
      <NumeraiLoader />
    </div>
    <PageNotFound v-else-if="roundNotFound"></PageNotFound>
    <div v-else>
      <div
        v-if="isRoundResolved"
        class="numerai-paper paper-success banner pa-4 my-2"
      >
        This round resolved on
        {{ formattedResolveTime }}. Scores are final and payouts have been made.
      </div>
      <div
        v-if="isRoundClosed"
        class="numerai-paper paper-pending banner pa-4 my-2"
      >
        This round has closed to submissions and will soon start scoring. It
        resolves
        {{ resolveTimeAgo }}.
      </div>
      <div
        v-if="isRoundScoring"
        class="numerai-paper paper-pending banner pa-4 my-2"
      >
        These scores and payout projections will update daily until the
        resolution of the round
        <strong>{{ resolveTimeAgo }}</strong>
      </div>
      <div class="d-flex">
        <div
          class="numerai-paper pa-4 d-flex flex-column justify-space-between"
        >
          <h1 class="heading">
            Round {{ number }} |
            {{ isRoundOpen ? 'Open' : null }}
            {{ isRoundClosed ? 'Closed' : null }}
            {{ isRoundScoring ? 'Scoring' : null }}
            {{ isRoundResolved ? 'Resolved' : null }}
          </h1>
          <table class="dates-table">
            <tr>
              <td>Open Time</td>
              <td class="dates">&nbsp;{{ formattedOpenTime }}</td>
            </tr>
            <tr>
              <td>Stake Deadline</td>
              <td class="dates">&nbsp;{{ formattedCloseStakingTime }}</td>
            </tr>
            <tr>
              <td>Submit Deadline</td>
              <td class="dates">&nbsp;{{ formattedCloseTime }}</td>
            </tr>
            <tr>
              <td>Resolve Date</td>
              <td class="dates">&nbsp;{{ formattedResolveTime }}</td>
            </tr>
          </table>

          <div class="d-flex justify-space-between round-buttons">
            <button
              class="numerai-button d-inline-flex align-center"
              @click="goToPrevRound"
            >
              <v-icon class="mr-1">mdi-arrow-left-bold</v-icon> Prev Round
            </button>
            <button
              v-if="!isRoundClosed"
              class="numerai-button d-inline-flex align-center"
              @click="goToNextRound"
            >
              Next Round <v-icon class="ml-1">mdi-arrow-right-bold</v-icon>
            </button>
          </div>
        </div>
        <div class="numerai-paper pa-4 ml-2">
          <table class="table_stats">
            <tr>
              <td>Staked models</td>
              <td class="value">
                {{ formattedTotalStakes }}
              </td>
            </tr>
            <tr>
              <td>NMR at stake</td>
              <td class="value">
                {{ formattedTotalNmrAtStake }}
              </td>
            </tr>
            <tr v-if="isRoundResolved">
              <td>Total Net Payouts</td>
              <td class="value">
                {{ formattedTotalNMmrPaid }}
              </td>
            </tr>
            <tr v-if="isRoundResolved">
              <td>Total Earned</td>
              <td class="value">
                {{ formattedTotalNMmrEarned }}
              </td>
            </tr>
            <tr v-if="isRoundResolved">
              <td>Total Burned</td>
              <td class="value">
                {{ formattedTotalNMmrBurned }}
              </td>
            </tr>
            <tr v-if="!isRoundOpen && !isRoundClosed">
              <td>Payout factor</td>
              <td class="value">
                {{ formattedPayoutFactor }}
              </td>
            </tr>
            <tr v-if="isRoundScoring">
              <td>Scores updated</td>
              <td>
                <strong>{{ scoresUpdatedAgo }}</strong>
              </td>
            </tr>
            <tr v-if="!isAppCryptoSignals()">
              <td>Target</td>
              <td>
                <strong>{{ roundTarget }}</strong>
              </td>
            </tr>
          </table>
        </div>
        <div
          v-if="
            allHistogramData && allHistogramData.length > 0 && isRoundResolved
          "
          class="numerai-paper pa-4 ml-2 flex-grow-1"
        >
          <h2 class="chart-heading">Score Distribution</h2>
          <RoundPerformanceHistogram
            header="Score Distribution"
            height="125px"
            :data1="stakedHistogramData"
            :data2="allHistogramData"
            :user-performances="userPerformances"
            :labels="labelsHistogramData"
            :tc="isTcStakingRound"
          />
        </div>
      </div>
      <div class="flexrow space_between mt-4">
        <div>
          <h1 class="numerai-section-heading models-heading">
            Leaderboard
          </h1>
        </div>
        <div class="search mr-2">
          <v-icon small class="search-icon">mdi-magnify</v-icon>
          <input v-model="filterBy" class="search-input" placeholder="Search" />
        </div>
      </div>
      <v-data-table
        class="leaderboard mt-2"
        :class="{ dark: isDarkMode, 'theme--dark': isDarkMode }"
        :headers="headers"
        :header-props="{ 'sort-icon': 'mdi-menu-down' }"
        :items="rows"
        :dark="isDarkMode"
        :search="search"
        :custom-filter="filterRows"
        :custom-sort="sortRows"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
      >
        <template v-slot:item.name="{ item }">
          <div class="model-name-mobile">
            <router-link :to="'/' + item.name" class="text_decoration_none">
              <div class="model-data numerai-link no-arrow">
                {{ item.rank }} {{ item.name && item.name.toUpperCase() }}
              </div>
            </router-link>
          </div>
          <div class="model-info pl-2">
            <div class="mr-4">{{ item.rank }}</div>
            <router-link :to="'/' + item.name" class="avatar" tag="a">
              <ProfileImage :src="item.profileUrl" />
            </router-link>
            <div class="ml-2">
              <router-link :to="'/' + item.name" class="text_decoration_none">
                <div class="modelname">{{ item.name }}</div>
              </router-link>
            </div>
          </div>
        </template>
      </v-data-table>
    </div>
    <Footer class="mt-4"></Footer>
  </div>
</template>
<script>
import NumeraiLoader from '@/components/shared/NumeraiLoader'
import PageNotFound from '@/pages/PageNotFound'
/* eslint-disable max-len */
import RoundPerformanceHistogram from '@/components/charts/RoundPerformanceHistogram'
import ProfileImage from '@/components/shared/ProfileImage'
/* eslint-enable max-len */
import {
  isAppClassic,
  isAppSignals,
  isAppCryptoSignals,
  getTournamentNumber,
  formatCurrency,
  formatScore,
} from '@/lib/utils'
import Analytics from '@/lib/analytics'
import { mapGetters, mapActions, mapState } from 'vuex'
import moment from 'moment'
import utils from '@/lib/utils'
import Footer from '@/components/hud/Footer'
import _ from 'lodash'

const STATUS = {
  OPEN: 'open',
  CLOSED: 'closed',
  SCORING: 'resolving',
  RESOLVED: 'resolved',
}

export default {
  components: {
    NumeraiLoader,
    PageNotFound,
    RoundPerformanceHistogram,
    Footer,
    ProfileImage,
  },
  props: {
    profile: { type: Object, default: null },
    isMobile: { type: Boolean, default: false },
    number: { type: String, default: null },
  },
  data() {
    return {
      roundNotFound: false,
      loading: true,
      payoutFactor: null,
      openTime: null,
      closeTime: null,
      closeStakingTime: null,
      resolveTime: null,
      models: [],
      headers: [],
      rows: [],
      loggedInModels: [],
      totalAtStake: null,
      totalStakes: null,
      allHistogramData: [],
      stakedHistogramData: [],
      labelsHistogramData: [],
      status: null,
      totalPayout: null,
      totalEarned: null,
      totalBurned: null,
      scoresUpdatedTime: null,
      sortBy: 'mmc',
      sortDesc: false,
      search: null,
    }
  },
  computed: {
    ...mapGetters(['nmrPrice', 'isLoggedIn', 'isDarkMode']),
    ...mapState(['accountInfo']),
    tournamentScoresConfig() {
      const filter = 'showRoundPage'
      let score_config = utils.getTournamentScoresConfig(
        getTournamentNumber(),
        filter,
      )
      // for signals remove CORR20 from the list of scores if >= 499
      //             remove TC if round >= 714
      if (isAppSignals()) {
        if (this.number >= 499) {
          delete score_config.corr
        }
        if (this.number >= 714) {
          delete score_config.tc
        }
      }
      // for numerai, remove CORR if round >= 484 and show v2_corr20 instead
      //              remove TC if round >= 650
      if (isAppClassic()) {
        if (this.number >= 650) {
          delete score_config.tc
        } else if (this.number > 484) {
          delete score_config.corr20
        } else {
          delete score_config.v2_corr20
        }
      }
      return score_config
    },
    isRoundOpen() {
      return this.status == STATUS.OPEN
    },
    isRoundClosed() {
      return this.status == STATUS.CLOSED
    },
    isRoundScoring() {
      return this.status == STATUS.SCORING
    },
    isRoundResolved() {
      return this.status == STATUS.RESOLVED
    },
    isTcStakingRound() {
      return (
        (isAppClassic() && this.number >= 311 && this.number < 650) ||
        (isAppSignals() && this.number >= 324)
      )
    },
    scoresLastUpdated() {
      return '28 hours ago'
    },
    formattedTotalNMmrPaid() {
      return formatCurrency(this.totalPayout) + ' NMR'
    },
    formattedTotalNMmrEarned() {
      return formatCurrency(this.totalEarned) + ' NMR'
    },
    formattedTotalNMmrBurned() {
      return formatCurrency(this.totalBurned) + ' NMR'
    },
    formattedTotalNmrAtStake() {
      return formatCurrency(this.totalAtStake) + ' NMR'
    },
    formattedTotalUSDAtStake() {
      return '$' + formatCurrency(this.totalAtStake * this.nmrPrice)
    },
    formattedTotalStakes() {
      if (!this.totalStakes) {
        return
      }
      return this.totalStakes.toLocaleString()
    },
    formattedOpenTime() {
      return moment.utc(this.openTime).format('D MMM YYYY HH:mm z')
    },
    formattedCloseStakingTime() {
      return moment.utc(this.closeStakingTime).format('D MMM YYYY HH:mm z')
    },
    formattedCloseTime() {
      return moment.utc(this.closeTime).format('D MMM YYYY HH:mm z')
    },
    formattedResolveTime() {
      return moment.utc(this.resolveTime).format('D MMM YYYY')
    },
    resolveTimeAgo() {
      return moment.utc(this.resolveTime).fromNow()
    },
    formattedPayoutFactor() {
      return formatScore(this.payoutFactor)
    },
    scoresUpdatedAgo() {
      return moment.utc(this.scoresUpdatedTime).fromNow()
    },
    roundTarget() {
      if (this.target == null) return '4D2L'
      return this.target.split('_')[1].toUpperCase()
    },
    userPerformances() {
      return this.loggedInModels.map(model => {
        const score = this.isTcStakingRound
          ? model.tc
          : model.corr20 + model.mmc
        return {
          modelName: model.modelName,
          value: score,
        }
      })
    },
    filterBy: {
      get() {
        return this.search
      },
      set: _.debounce(function(value) {
        this.search = value
      }, 300),
    },
  },
  watch: {
    number() {
      this.getRoundData()
    },
  },
  mounted() {
    Analytics.track('page view', {
      page: 'round page',
      tournament: getTournamentNumber(),
    })
  },
  async created() {
    if (this.isLoggedIn) {
      await this.fetchAccountInfo()
    }

    await this.getRoundData()

    if (this.isLoggedIn) {
      const modelIds = this.accountInfo.models.map(model => model.id)
      this.loggedInModels = this.models.filter(model =>
        modelIds.includes(model.id),
      )
    }
  },
  methods: {
    isAppClassic,
    isAppSignals,
    isAppCryptoSignals,
    ...mapActions(['fetchAccountInfo']),
    async getRoundData() {
      this.loading = true
      this.roundNotFound = false
      let _details
      try {
        _details = await this.$client.getRoundDetails(
          getTournamentNumber(),
          parseFloat(this.number),
        )
      } catch (err) {
        this.roundNotFound = true
        return
      }

      this.payoutFactor = _details.payoutFactor
      this.resolveTime = _details.roundResolveTime
      this.target = _details.roundTarget

      // Not all past rounds have histogram data
      if (_details.allHistogramData) {
        this.allHistogramData = _details.allHistogramData.counts
        this.stakedHistogramData = _details.stakedHistogramData.counts
        this.labelsHistogramData = _details.allHistogramData.bins
      }
      this.totalAtStake = _details.totalAtStake
      this.totalStakes = _details.totalStakes
      this.models = _details.models
      this.status = _details.status
      this.openTime = _details.openTime
      this.openTime = _details.openTime
      this.closeTime = _details.closeTime
      this.closeStakingTime = _details.closeStakingTime
      this.totalPayout = _details.totalPayout
      this.totalEarned = _details.totalEarned
      this.totalBurned = _details.totalBurned
      this.scoresUpdatedTime = _details.scoresUpdatedTime
      this.loading = false

      this.headers = [
        { value: 'name', text: 'NAME' },
        { value: 'stake', text: 'AT-RISK' },
      ]
      if (this.isRoundResolved || this.isRoundScoring) {
        const _scoreCols = Object.keys(this.tournamentScoresConfig).map(
          scoreKey => {
            return {
              text: this.tournamentScoresConfig[
                scoreKey
              ].displayName.toUpperCase(),
              align: 'start',
              sortable: true,
              value: scoreKey,
            }
          },
        )

        this.headers = this.headers.concat([..._scoreCols])

        if (this.isRoundResolved) {
          this.headers.push({
            value: 'payoutSettled',
            text: 'Payout',
            sortable: true,
          })
        }
      }
      this.rows = this.models.map(model => {
        let _row = {
          id: model.id,
          name: model.modelName,
          profileUrl: model.profileUrl,
          team: model.team,
          computeEnabled: model.computeEnabled,
        }
        _row._link = '/' + model.modelName

        _row.stake = Math.round((model.selectedStakeValue || 0.0) * 1000) / 1000
        _row.stake = formatCurrency(_row.stake) + ' NMR'

        if (this.status == STATUS.OPEN || this.status == STATUS.CLOSED) {
          return _row
        }
        Object.keys(this.tournamentScoresConfig).map(scoreKey => {
          let key = scoreKey
          if (this.tournamentScoresConfig[scoreKey].backendName) {
            key = this.tournamentScoresConfig[scoreKey].backendName
          }
          _row[scoreKey] = formatScore(model[key])
        })

        if (this.status == STATUS.RESOLVED) {
          _row.payoutSettled =
            Math.round((model.payoutSettled || 0.0) * 1000) / 1000
          _row.payoutSettled = formatCurrency(_row.payoutSettled) + ' NMR'
        } else if (this.status == STATUS.SCORING) {
          _row.payoutPending =
            Math.round((model.payoutPending || 0.0) * 1000) / 1000
          _row.payoutPending = formatCurrency(_row.payoutPending) + ' NMR'
        }
        return _row
      })
    },
    goToPrevRound() {
      this.goToRound(parseFloat(this.number) - 1)
    },
    goToNextRound() {
      this.goToRound(parseFloat(this.number) + 1)
    },
    goToRound(round) {
      this.$router.push(`/round/${round}`)
    },
    filterRows(value, search) {
      if (value === null) {
        return false
      }
      return value.includes(search)
    },
    sortRows(items, sortBy, sortDesc) {
      if (sortBy.length === 0) {
        this.sortBy = 'mmc'
        return items
      }
      let sortedItems = [...this.rows]
      if (sortBy[0] === 'name') {
        sortedItems.sort((a, b) => {
          if (!sortDesc[0]) {
            return b.name < a.name ? -1 : 1
          } else {
            return a.name < b.name ? -1 : 1
          }
        })
      } else {
        sortedItems.sort((a, b) => {
          const aVal = parseFloat(a[sortBy[0]])
          const bVal = parseFloat(b[sortBy[0]])
          if (aVal == null) {
            return sortDesc[0] ? 1 : -1
          } else if (bVal == null) {
            return sortDesc[0] ? -1 : 1
          } else if (sortDesc[0]) {
            return aVal < bVal ? -1 : 1
          } else {
            return bVal < aVal ? -1 : 1
          }
        })
      }

      sortedItems = sortedItems.map((item, index) => {
        if (sortDesc[0]) {
          item.rank = sortedItems.length - index
        } else {
          item.rank = index + 1
        }
        return item
      })
      let filtered_ids = items.map(item => item.id)
      return sortedItems.filter(item => filtered_ids.includes(item.id))
    },
  },
}
</script>
<style scoped>
.loading {
  width: 100%;
  height: 100%;
  min-height: 90vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.container {
  max-width: 900px;
}

.header {
  font-size: 1.2em;
  color: var(--color-secondary);
}

.dates-table {
  width: 100%;
}

.dates {
  font-size: 1.4em;
}

.heading {
  text-transform: none;
  font-weight: bold;
  font-size: 2.4rem;
  color: var(--color-primary);
  line-height: 1.2;
  text-wrap: nowrap;
}

.heading.status {
  font-weight: normal;
}

.heading.small {
  font-size: 1.6rem;
  font-family: var(--ui-font);
  font-weight: normal;
}

.banner {
  font-size: 1.2em;
}

.chart-heading {
  font-family: var(--ui-font);
  font-weight: 600;
  text-transform: none;
}

.table_stats td {
  padding-left: 0;
  padding-right: 16px;
  border-top: 0px solid var(--color-subtle) !important;
  text-wrap: nowrap;
}

.table_stats .value {
  font-weight: 600;
  padding-right: 0;
}
.table_stats tr {
  height: 2rem;
}

.leaderboard.dark {
  background-color: var(--paper-background-color);
}

.round-buttons .theme--light.v-icon {
  color: var(--color-anti-primary) !important;
}

.models-heading {
  display: inline-block;
}

.search {
  display: flex;
  align-items: center;
  border: 1px solid var(--color-button-control-background-border);
  border-radius: 0.2em;
  padding: 10px 5px;
  max-height: 40px;
}

.search-icon {
  color: var(--color-secondary);
}

.search-icon.theme--light.v-icon {
  color: var(--color-primary) !important;
}

.search-input {
  color: var(--color-secondary);
  margin-left: 10px;
  font-size: 16px;
}

.search-input:focus {
  outline: none;
}

.button-control {
  background-color: var(--color-button-control-background);
  border-color: var(--color-button-control-background-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.models-table.dark {
  background-color: var(--paper-background-color);
}

.model-data {
  font-family: var(--number-font);
  white-space: nowrap;
  font-size: 14px;
  color: var(--color-secondary);
}

.model-data.negative {
  margin-left: -8px;
}

.pickle-status-loader {
  width: 145px;
}

.stake {
  color: var(--color-text-nmr);
}

.model-info {
  display: none;
  min-width: 190px;
  height: 64px;
  align-items: center;
}

.avatar {
  padding-right: 8px;
}

td.modelname {
  text-align: left;
}

td {
  padding: 0 10px;
}

td.data {
  font-family: var(--number-font);
  padding-top: 8px;
  padding-bottom: 8px;
}

td.status {
  vertical-align: center;
  font-family: var(--ui-font);
  font-weight: 500;
}

.modelname {
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
  overflow-wrap: never;
  font-family: var(--brand-font);
  color: var(--color-secondary);
  vertical-align: center;
  display: flex;
  align-items: center;
}

.tc {
  color: var(--color-true-contribution);
}

.leftStakeSpacer {
  margin-left: -10px;
}

.filename {
  white-space: nowrap;
  text-align: left;
}

.more-button {
  display: flex;
  padding: 0 8px !important;
  min-width: 30px !important;
}

.more-button .v-icon {
  color: var(--color-secondary);
  font-size: 20px;
}

/* Overrides the default v-data-table line colors */
.theme--light.v-data-table >>> th,
.theme--light.v-data-table >>> td:not(.v-data-table__mobile-row) {
  border-bottom: thin solid rgba(0, 0, 0, 0.09) !important;
}

.theme--light.v-data-table >>> .v-data-footer {
  border-top: none;
}

@media screen and (min-width: 600px) {
  .search-input {
    width: 250px;
  }

  .model-info {
    display: flex;
  }

  .model-name-mobile {
    display: none;
  }

  .more-button {
    display: block;
  }
}

.loading-spinner {
  animation: loader 1s infinite linear;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
